/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { CollectionCell, CollectionData, RowActionDetail } from '../Common.types';

import { keyExtractor } from '#utils/PRIVATE_collectionUtils';

type ActionContextBuilder<K extends string | number, D, ContextType> = (
  index: number,
  key: K,
  data: D
) => ContextType;

/**
 * Helper function to get the row action context from a pointer or keyboard event
 */
export const getRowActionGestureContext = <
  K extends string | number,
  D,
  CellType extends CollectionCell<K>,
  ContextType
>(
  rootId: string,
  collectionData: CollectionData<K, D>[],
  targetElement: HTMLElement,
  rowSelector: string,
  getActionContext: ActionContextBuilder<K, D, ContextType>,
  targetCell?: CellType
) => {
  let rowKey: K | undefined | null;
  if (targetCell?.type === 'data') {
    rowKey = targetCell.rowKey;
  } else {
    rowKey = keyExtractor<K>(targetElement, `[${rowSelector}='${rootId}']`);
  }
  if (rowKey != null) {
    for (let i = 0; i < collectionData.length; i++) {
      const dataItem = collectionData[i];
      if (rowKey === dataItem.metadata.key) {
        return getActionContext(i, rowKey, dataItem.data);
      }
    }
  }
  return undefined;
};

/**
 * Helper function to handle a row action gesture from a pointer or keyboard event
 */
export const handleRowActionGesture = <
  K extends string | number,
  D,
  CellType extends CollectionCell<K>,
  ContextType
>(
  rootId: string,
  collectionData: CollectionData<K, D>[],
  onRowAction: (detail: RowActionDetail<ContextType>) => void,
  targetElement: HTMLElement,
  rowSelector: string,
  getActionContext: ActionContextBuilder<K, D, ContextType>,
  targetCell?: CellType
) => {
  const rowActionContext = getRowActionGestureContext(
    rootId,
    collectionData,
    targetElement,
    rowSelector,
    getActionContext,
    targetCell
  );
  if (rowActionContext != null) {
    onRowAction({ context: rowActionContext, target: targetElement });
    return true;
  }
  return false;
};
